// ==========================================================
// Google AdSense & H5 Games Ads Placement API Manager
// Includes Visual In-Game Sponsor Ad Player (Fallback & Demo)
// ==========================================================

import { EconomyManager } from './EconomyManager';
import { sounds } from '../audio/SoundEffects';

declare global {
  interface Window {
    adsbygoogle?: any[];
    adBreak?: (params: any) => void;
    adConfig?: (params: any) => void;
  }
}

export class AdManager {
  private static isInitialized = false;

  /**
   * Initialize H5 Games Ads Ad Placement API Configuration
   */
  public static init() {
    if (this.isInitialized) return;

    if (typeof window !== 'undefined' && window.adConfig) {
      try {
        window.adConfig({
          sound: 'on',
          preloadAdBreaks: 'on'
        });
      } catch (e) {
        console.warn('AdConfig init warning:', e);
      }
      this.isInitialized = true;
    }
  }

  /**
   * Request and display a Rewarded Video Ad via Google H5 Games Ads or In-Game Player
   */
  public static async showRewardedAd(onRewardGranted?: () => void): Promise<boolean> {
    return new Promise((resolve) => {
      // 1. Try Google H5 Games Ads first if active
      let googleAdTriggered = false;
      if (typeof window !== 'undefined' && typeof window.adBreak === 'function') {
        try {
          window.adBreak({
            type: 'reward',
            name: 'reward_coins_500',
            beforeAd: () => {
              googleAdTriggered = true;
              sounds.stopContinuousSounds();
            },
            afterAd: () => {
              // Google ad finished
            },
            beforeReward: (showAdFn: () => void) => {
              googleAdTriggered = true;
              showAdFn();
            },
            adDismissed: () => {
              resolve(false);
            },
            adViewed: () => {
              EconomyManager.claimRewardAd();
              sounds.playFanfare(3);
              if (onRewardGranted) onRewardGranted();
              resolve(true);
            }
          });
        } catch (e) {
          console.warn('Google adBreak error, falling back to in-game ad player:', e);
        }
      }

      // 2. If Google ad is not playing (e.g. pending site review or offline), show high-quality in-game ad player
      setTimeout(() => {
        if (!googleAdTriggered) {
          this.playInGameAdModal(onRewardGranted, resolve);
        }
      }, 300);
    });
  }

  /**
   * In-Game Visual Sponsor Ad Player with Countdown & Progress
   */
  private static playInGameAdModal(
    onRewardGranted?: () => void,
    onComplete?: (success: boolean) => void
  ) {
    const modalEl = document.getElementById('modal-ad-player');
    const timerEl = document.getElementById('ad-timer-countdown');
    const progressFillEl = document.getElementById('ad-progress-fill');
    const btnClaim = document.getElementById('btn-ad-claim-reward');
    const btnClose = document.getElementById('btn-close-ad');

    if (!modalEl || !timerEl || !progressFillEl || !btnClaim) {
      // Fallback
      EconomyManager.claimRewardAd();
      sounds.playFanfare(3);
      if (onRewardGranted) onRewardGranted();
      if (onComplete) onComplete(true);
      return;
    }

    modalEl.classList.remove('hidden');
    btnClaim.classList.add('hidden');
    if (btnClose) btnClose.classList.add('hidden');
    progressFillEl.style.width = '0%';

    let totalDuration = 5; // 5 seconds ad
    let remaining = totalDuration;
    timerEl.textContent = `${remaining}s`;

    sounds.stopContinuousSounds();

    const interval = window.setInterval(() => {
      remaining -= 1;
      const progressPercent = ((totalDuration - remaining) / totalDuration) * 100;
      progressFillEl.style.width = `${progressPercent}%`;

      if (remaining > 0) {
        timerEl.textContent = `${remaining}s`;
      } else {
        clearInterval(interval);
        timerEl.textContent = '✅ 完了';
        btnClaim.classList.remove('hidden');
        if (btnClose) btnClose.classList.remove('hidden');

        // Auto grant or on claim click
        const claimHandler = () => {
          btnClaim.removeEventListener('click', claimHandler);
          modalEl.classList.add('hidden');
          EconomyManager.claimRewardAd();
          sounds.playFanfare(3);
          if (onRewardGranted) onRewardGranted();
          if (onComplete) onComplete(true);
        };

        btnClaim.addEventListener('click', claimHandler);
        if (btnClose) btnClose.addEventListener('click', claimHandler);
      }
    }, 1000);
  }
}
